import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ENGAGEMENT_ID, useBranches } from '../../context/BranchContext';
import { BranchIcon, CheckIcon, ChevronIcon } from '../common/icons';

function summary(selected, byId) {
  if (!selected.length) return 'No branch selected';
  const names = selected.map((id) => byId[id]?.name).filter(Boolean);
  if (names.length <= 2) return names.join(', ');
  return `${names[0]} + ${names.length - 1} more`;
}

/** Header dropdown: tick Demo Engagement and any Nyafaz branches. */
export default function BranchMultiSelect() {
  const { all, byId, selected, toggle, selectAll, clear, loading, error } = useBranches();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const [engagement, branches] = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = (b) =>
      !needle || [b.name, b.area, b.city].filter(Boolean).some((t) => String(t).toLowerCase().includes(needle));
    return [all.find((b) => b.id === ENGAGEMENT_ID), all.filter((b) => b.type === 'branch' && match(b))];
  }, [all, q]);

  const row = (b, sub) => {
    const on = selected.includes(String(b.id));
    return (
      <button
        key={b.id}
        type="button"
        role="menuitemcheckbox"
        aria-checked={on}
        className={`dp-ms-option${on ? ' is-on' : ''}`}
        onClick={() => toggle(b.id)}
      >
        <span className="dp-ms-check">{on ? <CheckIcon size={14} /> : null}</span>
        <span className="dp-ms-text">
          <strong>{b.name}</strong>
          {sub ? <small>{sub}</small> : null}
        </span>
      </button>
    );
  };

  return (
    <div className="dp-ms" ref={ref}>
      <button
        type="button"
        className="dp-ms-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <BranchIcon size={16} />
        <span className="dp-ms-label">
          <small>Branches</small>
          <span className="dp-ellipsis">{loading ? 'Loading…' : summary(selected, byId)}</span>
        </span>
        <span className="dp-ms-count">{selected.length}</span>
        <ChevronIcon size={16} open={open} />
      </button>
      {open ? (
        <div className="dp-ms-panel" role="menu">
          {error ? <p className="dp-ms-error">{error}</p> : null}
          {engagement ? (
            <div className="dp-ms-group">
              <p className="dp-ms-heading">Appointments</p>
              {row(engagement, 'WhatsApp, Instagram, voice & walk-in bookings')}
            </div>
          ) : null}
          <div className="dp-ms-group">
            <div className="dp-ms-heading-row">
              <p className="dp-ms-heading">Nyafaz branches</p>
              <span>
                <button type="button" className="dp-link" onClick={selectAll}>
                  All
                </button>
                <button type="button" className="dp-link" onClick={clear}>
                  None
                </button>
              </span>
            </div>
            <input
              className="dp-input dp-ms-search"
              placeholder="Search branch or city"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="dp-ms-list">
              {branches.length
                ? branches.map((b) => row(b, [b.area, b.city].filter(Boolean).join(', ')))
                : <p className="dp-ms-empty">No branch matches.</p>}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
